import { fragments as automatedNotesWrapperFragments } from "../AutomatedNotesWrapper/graph";
import { filter } from "graphql-anywhere";
import { makeStyles } from "@mui/styles";
import { fragments as notesWrapperFragments } from "../NotesWrapper/graph";
import { fragments as orderStatusHeaderFragments } from "../OrderStatusHeader/graph";
import { fragments as productionJobDetailFragments } from "../ProductionJobDetail/graph";
import { fragments as productionJobListFragments } from "../ProductionJobList/graph";
import { useRouter } from "found";
import AutomatedNotesWrapper from "../AutomatedNotesWrapper";
import CurrentViewers from "../CurrentViewers";
import NotesWrapper from "../NotesWrapper";
import OrderStatusHeader from "../OrderStatusHeader";
import OrderStatusHistorySummary from "../../application/apps/OrderApp/components/OrderStatusHistorySummary";
import PendingStatusView from "../PendingStatusView";
import ProductionJobDetail from "../ProductionJobDetail";
import ProductionJobList from "../ProductionJobList";
import React, { useCallback, useEffect } from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import withBarcodePivot from "../BarcodePivot/withBarcodePivot";

const useStyles = makeStyles(theme => ({
  absoluteWrapper: {
    position: "absolute",
    top: 48,
    bottom: 0,
    left: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column"
  },

  appBarWrapper: {
    zIndex: 1
  },

  content: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0
  },

  scrollableContent: {
    overflow: "auto",
    minHeight: 0,
    display: "flex"
  },

  productionJobListContainer: {
    overflowY: "scroll",
    borderRight: `1px solid ${theme.palette.divider}`,
    width: "100%",
    flex: "1 1 15%",
    minWidth: 300
  },

  notesHistoryContainer: {
    flex: "1 1 25%",
    overflowY: "scroll"
  },

  productionJobDetailContainer: {
    flex: "1 1 60%",
    overflowY: "scroll",
    borderRight: `1px solid ${theme.palette.divider}`
  },

  tab: {
    color:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.7)"
        : "rgba(0, 0, 0, 0.54)",
    margin: theme.spacing(1)
  }
}));

const OrderDetailWithStatusHeader = ({
  acknowledgeOrderProductionJob,
  acknowledgeStockContainer,
  children,
  destinationRoute,
  errorMessage,
  isArtApp,
  lastOrderProductionJobScanned,
  lastStockContainerScanned,
  loading,
  onRequestClose,
  order,
  orderStatusView,
  shouldShowArtNotes = true,
  shouldShowClaimButton,
  shouldShowCustomerArtworkWithNotes,
  shouldShowInkColors,
  shouldShowSalesNotes,
  viewer
}) => {
  const classes = useStyles();
  const { router } = useRouter();

  // States:
  const [selectedAuxiliaryTabIndex, setSelectedAuxiliaryTabIndex] =
    React.useState(0);
  const [selectedProductionJobIndex, setSelectedProductionJobIndex] =
    React.useState(0);

  // Handlers:
  const handleAuxiliaryTabChange = (event, selectedIndex) =>
    setSelectedAuxiliaryTabIndex(selectedIndex);
  const handleOrderStatusAppBarRequestClose = () => onRequestClose();
  const handleProductionJobListSelection = useCallback(
    selectedIndex => setSelectedProductionJobIndex(selectedIndex),
    [setSelectedProductionJobIndex]
  );

  useEffect(() => {
    if (order) {
      const { productionJobs } = order;
      let selectedProductionJobIndex = 0;
      const orderProductionJobId = new URLSearchParams(
        document.location.search
      ).get("orderProductionJobId");
      if (orderProductionJobId) {
        selectedProductionJobIndex = productionJobs
          .map(({ id }) => id)
          .indexOf(orderProductionJobId);
        handleProductionJobListSelection(selectedProductionJobIndex);
      }
    }
  }, [order, handleProductionJobListSelection]);

  useEffect(() => {
    if (lastOrderProductionJobScanned) {
      router.replace(
        `${destinationRoute}/${lastOrderProductionJobScanned.order.id}`
      );
      acknowledgeOrderProductionJob();
    }
  }, [
    lastOrderProductionJobScanned,
    acknowledgeOrderProductionJob,
    destinationRoute,
    router
  ]);

  useEffect(() => {
    if (lastStockContainerScanned) {
      router.replace(
        `${destinationRoute}/${lastStockContainerScanned.orderProductionJob.order.id}`
      );
      acknowledgeStockContainer();
    }
  }, [
    lastStockContainerScanned,
    acknowledgeStockContainer,
    destinationRoute,
    router
  ]);

  return (
    <div>
      {children}
      {loading ? (
        <div>
          <PendingStatusView status="Loading" />
        </div>
      ) : errorMessage ? (
        <Typography align="center" variant="body2" color="error">
          {errorMessage}
        </Typography>
      ) : (
        <div className={classes.absoluteWrapper}>
          <div className={classes.content}>
            <div className={classes.appBarWrapper}>
              <OrderStatusHeader
                onRequestClose={handleOrderStatusAppBarRequestClose}
                order={filter(orderStatusHeaderFragments.order, order)}
                orderStatusView={orderStatusView}
                isArtApp={isArtApp}
              />
            </div>
            <div className={classes.scrollableContent}>
              <div className={classes.productionJobListContainer}>
                <ProductionJobList
                  onProductionJobSelected={handleProductionJobListSelection}
                  productionJobs={filter(
                    productionJobListFragments.productionJobs,
                    order.productionJobs
                  )}
                  selectedIndex={selectedProductionJobIndex}
                  shouldShowClaimButton={shouldShowClaimButton}
                />
              </div>
              <div className={classes.productionJobDetailContainer}>
                <ProductionJobDetail
                  isArtApp={isArtApp}
                  productionJob={filter(
                    productionJobDetailFragments.productionJob,
                    order.productionJobs[selectedProductionJobIndex]
                  )}
                  shouldShowInkColors={shouldShowInkColors}
                  shouldShowCustomerArtworkWithNotes={
                    shouldShowCustomerArtworkWithNotes
                  }
                />
              </div>
              <div className={classes.notesHistoryContainer}>
                <Tabs
                  indicatorColor="primary"
                  onChange={handleAuxiliaryTabChange}
                  scrollButtons="auto"
                  textColor="primary"
                  value={selectedAuxiliaryTabIndex}
                  variant="scrollable"
                >
                  <Tab className={classes.tab} label="Notes" />
                  <Tab
                    className={classes.tab}
                    label={`Automated Notes (${order.automatedNotesCollection.length})`}
                  />
                  <Tab className={classes.tab} label="Status History" />
                  <Tab className={classes.tab} label="Current Viewers" />
                </Tabs>
                {selectedAuxiliaryTabIndex === 0 && (
                  <div>
                    <NotesWrapper
                      shouldShowArtNotes={shouldShowArtNotes}
                      shouldShowSalesNotes={isArtApp && shouldShowSalesNotes}
                      order={filter(notesWrapperFragments.order, order)}
                    />
                  </div>
                )}
                {selectedAuxiliaryTabIndex === 1 && (
                  <div>
                    <AutomatedNotesWrapper
                      order={filter(
                        automatedNotesWrapperFragments.order,
                        order
                      )}
                    />
                  </div>
                )}
                {selectedAuxiliaryTabIndex === 2 && (
                  <div>
                    <OrderStatusHistorySummary orderId={order.id} />
                  </div>
                )}
                <div
                  style={{
                    display: selectedAuxiliaryTabIndex === 3 ? "block" : "none"
                  }}
                >
                  <CurrentViewers channel={order.id} viewer={viewer} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withBarcodePivot(OrderDetailWithStatusHeader);
